'use client';

import { Button, Icon, Typography } from '@workspace/ui/common';
import { cn, getBackgroundClass, getLinkUrl, shouldOpenInNewTab } from '@workspace/lib';
import { stripInvisibles } from '@workspace/constants';
import { processHeading } from '@workspace/lib/heading-utils';
import { applicationsHeadingStyleVariant } from '@workspace/blocks/PageApplications/applications.variants';
import { pageApplicationsVariants } from '@workspace/blocks/PageApplications/applications.variants';
import type { ApplicationsBlockProps } from './application.types';
import { getHeadingAlignment } from './layout';
import { ApplicationList, renderButtons } from './ui';

export function Applications({ block }: ApplicationsBlockProps) {
  if (block?.enabled === false) return null;
  const { heading, subheading, headingAlign, buttons = [], items = [], backgroundColor, cta } = block || {};
  const headingAlignment = getHeadingAlignment(
    headingAlign || block?.headingAlignment || block?.textAlignment || block?.align,
  );

  const { text: resolvedHeadingText } = processHeading(heading);
  const { text: resolvedSubheadingText } = processHeading(subheading);
  const backgroundClass = getBackgroundClass(backgroundColor);
  const ctaLabel = stripInvisibles(cta?.label || '').trim();
  const ctaResolvedUrl = getLinkUrl(cta?.link);
  const ctaUrl = ctaResolvedUrl === '#' ? '' : stripInvisibles(ctaResolvedUrl).trim();
  const ctaOpenInNewTab = shouldOpenInNewTab(cta?.link);

  const {
    root,
    container,
    header,
    headingGroup,
    heading: headingClass,
    subheading: subheadingClass,
    content,
    ctaWrapper,
    cta: ctaClass,
    ctaText,
    ctaIcon,
  } = pageApplicationsVariants({ alignment: headingAlignment });

  return (
    <div className={cn('w-full', root(), backgroundClass)}>
      <div className={cn('porta-container flex w-full flex-col', container())}>
        {(resolvedHeadingText || resolvedSubheadingText) && (
          <div className={cn('relative flex w-full', header())}>
            <div className={cn('flex w-full flex-col', headingGroup())}>
              {resolvedHeadingText && (
                <Typography variant="h2" className={headingClass()} styleVariant={applicationsHeadingStyleVariant}>
                  {resolvedHeadingText}
                </Typography>
              )}
              {resolvedSubheadingText && (
                <Typography variant="bodyLg" className={subheadingClass()}>
                  {resolvedSubheadingText}
                </Typography>
              )}
            </div>
          </div>
        )}
        <div className={cn('flex flex-col', content())}>
          {Array.isArray(items) && items.length > 0 && <ApplicationList items={items} />}

          {renderButtons(buttons)}
        </div>
        {ctaLabel && ctaUrl && (
          <div className={cn('w-full', ctaWrapper())}>
            <Button
              variant="text"
              url={ctaUrl}
              openInNewTab={ctaOpenInNewTab}
              overrideClasses
              className={cn('group inline-flex items-center justify-center', ctaClass())}
            >
              <span className={ctaText()}>{ctaLabel}</span>
              <Icon
                name="arrowRightIcon"
                size={24}
                className={cn(ctaIcon(), 'transition-transform duration-300 ease-in-out group-hover:translate-x-1')}
              />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
